import { FuseNavigationItem } from '@fuse/components/navigation';
import { RoleEnum } from 'app/core/roles/role.types';

/**
 * The sidebar menu. A new screen gets its entry here, once, under the group for its area. An item
 * with meta.roles shows only to those roles - see navigationFor.
 */
export const MAIN_NAVIGATION: FuseNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'mat_outline:dashboard',
        link: '/dashboard',
    },
    {
        id: 'learners',
        title: 'Learners',
        type: 'group',
        children: [
            {
                id: 'students',
                title: 'Students',
                type: 'basic',
                icon: 'mat_outline:school',
                link: '/students',
            },
            {
                id: 'schools',
                title: 'Schools',
                type: 'basic',
                icon: 'mat_outline:location_city',
                link: '/schools',
            },
        ],
    },
    {
        id: 'teaching',
        title: 'Teaching',
        type: 'group',
        children: [
            {
                id: 'class-groups',
                title: 'Classes',
                type: 'basic',
                icon: 'mat_outline:event_note',
                link: '/class-groups',
            },
            {
                id: 'lessons',
                title: 'Schedule',
                type: 'basic',
                icon: 'mat_outline:calendar_today',
                link: '/lessons',
            },
            {
                id: 'attendance',
                title: 'Attendance',
                type: 'basic',
                icon: 'mat_outline:fact_check',
                link: '/attendance',
            },
            {
                id: 'registers',
                title: 'Registers',
                type: 'basic',
                icon: 'mat_outline:checklist',
                link: '/registers',
                meta: { roles: [RoleEnum.Role.Administrator] },
            },
            {
                // For instructors too: it is how they reach their classes' reports.
                id: 'progress-reports',
                title: 'Progress reports',
                type: 'basic',
                icon: 'mat_outline:assignment',
                link: '/progress-reports',
            },
            {
                id: 'calendar-exceptions',
                title: 'Calendar exceptions',
                type: 'basic',
                icon: 'mat_outline:event_busy',
                link: '/calendar-exceptions',
            },
        ],
    },
    {
        id: 'finance',
        title: 'Finance',
        type: 'group',
        meta: { roles: [RoleEnum.Role.Administrator] },
        children: [
            {
                id: 'finance.statements',
                title: 'Bank statements',
                type: 'basic',
                icon: 'mat_outline:upload_file',
                link: '/finance/statements',
            },
            {
                id: 'finance.registers',
                title: 'Payment registers',
                type: 'basic',
                icon: 'mat_outline:receipt_long',
                link: '/finance/registers',
            },
            {
                id: 'finance.outstanding',
                title: 'Outstanding',
                type: 'basic',
                icon: 'mat_outline:pending_actions',
                link: '/finance/outstanding',
            },
            {
                id: 'finance.expenses',
                title: 'Expense claims',
                type: 'basic',
                icon: 'mat_outline:receipt',
                link: '/finance/expenses',
            },
        ],
    },
    {
        id: 'administration',
        title: 'Administration',
        type: 'group',
        children: [
            {
                id: 'instructors',
                title: 'Instructors',
                type: 'basic',
                icon: 'mat_outline:school',
                link: '/instructors',
            },
            {
                id: 'users',
                title: 'Users',
                type: 'basic',
                icon: 'mat_outline:people',
                link: '/users',
            },
        ],
    },
];

/**
 * The menu the signed-in user may use. Items whose meta.roles the user holds none of are left
 * out, children included, and so is a group left with no children. Hiding them is presentation:
 * the API refuses the screens' data to anyone else regardless.
 */
export function navigationFor(
    items: FuseNavigationItem[],
    isInRole: (roles: string[]) => boolean
): FuseNavigationItem[] {
    return items
        .filter((item) => !item.meta?.roles || isInRole(item.meta.roles))
        .map((item) =>
            item.children
                ? { ...item, children: navigationFor(item.children, isInRole) }
                : item
        )
        .filter((item) => !item.children || item.children.length > 0);
}
